import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PlanService } from 'src/app/nutrigenic/services/plan/plan.service';
import { Notify } from 'notiflix/build/notiflix-notify-aio';

@Component({
    selector: 'app-order-card',
    templateUrl: './order-card.component.html',
    styleUrls: ['./order-card.component.scss']
})
export class OrderCardComponent implements OnInit {

    @Input() order: any;
    @Input() showDetails: boolean = true;
    @Input() showSkip: boolean = false;
    @Input() showCancel: boolean = false;

    @Output() details = new EventEmitter<number>();
    @Output() payment = new EventEmitter<any>();
    @Output() actionCompleted = new EventEmitter<void>();

    pauseSubscriptionPopUpVisibility: boolean = false;
    cancelSubscriptionPopUpVisibility: boolean = false;
    pauseSubscriptionForm: FormGroup;
    pauseDates: any[] = [];
    filteredPauseDates: any[] = [];
    cancellationDates: any[] = [];

    orderStatusList = [
            { id: 1, text: 'Completed', icon: 'assets/images/checkmark-circle.svg', color: '#DFDFEF' },
            { id: 2, text: 'In Revision', icon: 'assets/images/alert.svg', color: '#FCDAB1' },
            { id: 4, text: 'Suspended', icon: 'assets/images/alert.svg', color: '#FCBBB1' },
            { id: 5, text: 'Active', icon: 'assets/images/workout-run.svg', color: '#CEF3DF' },
            { id: 7, text: 'Paused', icon: 'assets/images/pause-circle.svg', color: '#FCDAB1' },
            { id: 8, text: 'Canceled', icon: 'assets/images/alert.svg', color: '#FCBBB1' }
        ];

    constructor(
        private fb: FormBuilder,
        private planService: PlanService
    ) {}

    ngOnInit() {
        this.pauseSubscriptionForm = this.fb.group({
            pauseFromDate: ['', [Validators.required]],
            pauseToDate: ['', [Validators.required]]
        });
    }

    getOrderStatus(statusId: number) {
        return this.orderStatusList.find((s: any) => s.id === statusId) || {
            color: '#CCCCCC',
            icon: '',
            text: 'Unknown'
        };
    }

    isSubscriptionActive(order: any) {
        return order.status == 5;
    }

    isSubscriptionFlexi(order: any) {
        return order.order_type == 'flexi';
    }

    openPauseSubscription() {
        this.pauseSubscriptionForm.reset();
        const orderDetails = (this.order.order_detail || this.order.order_details).filter((x: any) => !x.supplement_id);
        const grouped = Object.values(orderDetails.reduce((acc: any, cur: any) => {
            acc[cur.week] = acc[cur.week] || { week: cur.week, details: [] };
            acc[cur.week].details.push(cur);
            return acc;
        }, {}));
        this.createPauseDatesMenu(grouped);
        this.pauseSubscriptionPopUpVisibility = true;
    }

    closePauseSubscription() {
        this.pauseSubscriptionPopUpVisibility = false;
    }

    onPauseFromDateChange(selectedDate: string) {
        if (selectedDate) {
            const selectedDateObj = new Date(selectedDate);
            this.filteredPauseDates = this.pauseDates.filter(date => new Date(date.value) > selectedDateObj);
            this.pauseSubscriptionForm.get('pauseToDate')?.reset();
        } else {
            this.filteredPauseDates = this.pauseDates;
        }
    }

    sendSubscriptionPause() {
        if (this.pauseSubscriptionForm.valid) {
            const pauseFrom = this.pauseSubscriptionForm.get('pauseFromDate')?.value;
            const pauseTo = this.pauseSubscriptionForm.get('pauseToDate')?.value;
            this.planService.pauseSubscription(
                this.order.id,
                pauseFrom,
                pauseTo
            ).subscribe({
                next: () => {
                    this.pauseSubscriptionPopUpVisibility = false;
                    this.pauseSubscriptionForm.reset();
                    this.order.pause_from = this.formatDateLocal(new Date(pauseFrom));
                    this.order.pause_until = this.formatDateLocal(new Date(pauseTo));
                    Notify.success('Pause Subscription applied', { position: 'right-top', timeout: 3000 });
                    this.actionCompleted.emit();
                },
                error: (error) => {
                    this.handleError(error);
                }
            });
        }
    }

    openCancelSubscription() {
        const orderDetails = (this.order.order_detail || this.order.order_details).filter((x: any) => !x.supplement_id && x.type !== 'addon' && x.meal?.type !== 'addon');
        const grouped: any[] = Object.values(orderDetails.reduce((acc: any, cur: any) => {
            acc[cur.week] = acc[cur.week] || { week: cur.week, details: [] };
            acc[cur.week].details.push(cur);
            return acc;
        }, {}));
        this.createCancelationDatesMenu(grouped);
        this.cancelSubscriptionPopUpVisibility = true;
    }

    closeCancelSubscription() {
        this.cancelSubscriptionPopUpVisibility = false;
    }

    onSubscriptionCancelled(cancelDate: string) {
        this.cancelSubscriptionPopUpVisibility = false;
        this.order.cancel_from = this.formatDateLocal(new Date(cancelDate));
        Notify.success('Cancelation request sent', { position: 'right-top', timeout: 3000 });
        this.actionCompleted.emit();
    }

    private createPauseDatesMenu(orderDetails: any[]) {
        this.pauseDates = this.getDeliveryDates(orderDetails);
        this.filteredPauseDates = this.pauseDates;
    }

    private createCancelationDatesMenu(orderDetails: any[]) {
        this.cancellationDates = this.getDeliveryDatesBeginningWeek(orderDetails);
    }

    private getDeliveryDates(orderDetails: any[]) {
        const now = new Date();
        now.setDate(now.getDate() + 4);
        const seen = new Set<string>();
        const dates: any[] = [];
        orderDetails.forEach((item: any) => {
            if (item.week < 3) return;
            item.details.forEach((detail: any) => {
                const deliveryDate = new Date(detail.delivery_date);
                if (deliveryDate > now) {
                    const dateStr = deliveryDate.toISOString();
                    if (!seen.has(dateStr)) {
                        seen.add(dateStr);
                        dates.push({ label: deliveryDate.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' }), value: dateStr });
                    }
                }
            });
        });
        return dates;
    }

    private getDeliveryDatesBeginningWeek(orderDetails: any[]) {
        const now = new Date();
        now.setDate(now.getDate() + 4);
        return orderDetails
            .map((item: any) => {
                if (item.week < 3) return null;
                const deliveryDate = new Date(item.details[0].delivery_date);
                return deliveryDate > now ? { label: deliveryDate.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' }), value: deliveryDate.toISOString() } : null;
            })
            .filter((date: any) => date !== null);
    }

    private handleError(error: any): void {
        if (error && error.type && error.type === 'validation') {
            let errors = error.message;
            Object.entries(errors).forEach(([_key, value]) => {
                const msg = (value as string[])[0];
                Notify.failure(msg, { position: 'right-top', timeout: 3000 });
            });
        } else {
            const msg = error?.message ?? 'Internal error - Could not complete the operation';
            Notify.failure(msg, { position: 'right-top', timeout: 3000 });
        }
    }

    private formatDateLocal(date: Date): string {
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        return `${day}.${month}.${date.getFullYear()}`;
    }
}
